/** How long a grant is good for. A call is made at once or not at all. */
export const GRANT_TTL_SECONDS = 60;
const encoder = new TextEncoder();
function b64url(bytes) {
    let binary = '';
    for (const b of bytes)
        binary += String.fromCharCode(b);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromB64url(text) {
    const padded = text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4);
    const binary = atob(padded);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1)
        out[i] = binary.charCodeAt(i);
    return out;
}
async function hmacKey(secret) {
    if (secret.length < 32) {
        throw new Error('a grant signing key is at least 32 characters; a short one is guessable');
    }
    return crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
        'sign',
        'verify',
    ]);
}
/** Canonical: keys sorted at every level, so the same claims always sign the same bytes. */
function canonical(value) {
    if (Array.isArray(value))
        return `[${value.map(canonical).join(',')}]`;
    if (value && typeof value === 'object') {
        return `{${Object.keys(value)
            .sort()
            .filter((k) => value[k] !== undefined)
            .map((k) => `${JSON.stringify(k)}:${canonical(value[k])}`)
            .join(',')}}`;
    }
    return JSON.stringify(value);
}
export async function signGrant(claims, secret) {
    const payload = encoder.encode(canonical(claims));
    const signature = new Uint8Array(await crypto.subtle.sign('HMAC', await hmacKey(secret), payload));
    return `${b64url(payload)}.${b64url(signature)}`;
}
/**
 * Check a grant. The signature is verified before a byte of the claims is
 * trusted, and in constant time (WebCrypto's verify); then the version and
 * the expiry. Single use is the redeemer's to enforce, against its store.
 */
export async function verifyGrant(grant, secret, nowSeconds = Math.floor(Date.now() / 1000)) {
    const parts = grant.split('.');
    if (parts.length !== 2 || !parts[0] || !parts[1])
        return { ok: false, reason: 'malformed' };
    let payload;
    let signature;
    try {
        payload = fromB64url(parts[0]);
        signature = fromB64url(parts[1]);
    }
    catch {
        return { ok: false, reason: 'malformed' };
    }
    // Cast for the lib types only: a Uint8Array is a BufferSource at runtime
    // in the Worker and in Node alike.
    const valid = await crypto.subtle.verify('HMAC', await hmacKey(secret), signature, payload);
    if (!valid)
        return { ok: false, reason: 'bad_signature' };
    let claims;
    try {
        claims = JSON.parse(new TextDecoder().decode(payload));
    }
    catch {
        return { ok: false, reason: 'malformed' };
    }
    if (claims.v !== 1)
        return { ok: false, reason: 'unsupported' };
    if (typeof claims.exp !== 'number' || claims.exp < nowSeconds)
        return { ok: false, reason: 'expired' };
    if (typeof claims.rid !== 'string' || !Array.isArray(claims.refs) || typeof claims.tool !== 'string') {
        return { ok: false, reason: 'malformed' };
    }
    return { ok: true, claims };
}
/** What the gateway says about its tool-call protocol, at `GET /v1/protocol`. */
export const TOOL_CALL_PROTOCOL = 'decision/2';
/**
 * Where an agent reports what its call measured (ADR-0099): the response
 * headers the allow named in `measure`, for that `requestId` alone.
 */
export const TOOL_CALL_SETTLE_PATH = '/v1/tool-call/settle';
//# sourceMappingURL=grant.js.map